import React, { useEffect, useRef, useState } from "react";
import { FaTimes, FaExternalLinkAlt, FaTag } from "./icons";
import "./ProjectModal.css";

const ProjectModal = React.memo(({ project, onClose }) => {
  const modalContentRef = useRef(null);
  const [scrollData, setScrollData] = useState({ 
    thumbHeight: 20, 
    thumbTop: 0,
    trackHeight: 100
  });

  const updateScrollbar = () => {
    if (modalContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
      
      if (scrollHeight > 0 && clientHeight > 0) {
        const scrollableHeight = scrollHeight - clientHeight;
        
        // Thumb height represents visible area vs TOTAL content height
        const thumbHeight = Math.max(5, (clientHeight / scrollHeight) * 100);
        
        // Calculate track height - represents FULL content height
        // Track should always represent the full content, not just visible area
        const trackHeight = Math.max(100, (scrollHeight / clientHeight) * 100);
        
        // Calculate thumb position - work within the extended track
        let thumbTop = 0;
        if (scrollableHeight > 0) {
          // Calculate scroll progress (0 to 1)
          const scrollProgress = scrollTop / scrollableHeight;
          
          // Max thumb position is within the track, not just modal height
          const maxThumbTop = trackHeight - thumbHeight;
          
          // Map scroll progress to thumb position within the extended track
          thumbTop = scrollProgress * maxThumbTop;
          
          // CRITICAL: Ensure thumb reaches the very bottom when at the end
          if (scrollTop >= scrollableHeight - 0.1) {
            thumbTop = maxThumbTop;
          }
        }
        
        // Clamp values to prevent overflow
        const clampedThumbHeight = Math.min(thumbHeight, 100);
        const clampedThumbTop = Math.max(0, Math.min(thumbTop, trackHeight - clampedThumbHeight));
        
        // Calculate visual cues for extended content
        const contentExtendsBeyond = scrollTop < scrollableHeight - 10 ? 1 : 0;
        const contentExtendsAbove = scrollTop > 10 ? 1 : 0;
        
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          setScrollData({
            thumbHeight: clampedThumbHeight,
            thumbTop: clampedThumbTop,
            trackHeight: trackHeight
          });
          
          // Apply visual cues and track height via CSS custom properties
          if (modalContentRef.current) {
            modalContentRef.current.style.setProperty('--content-extends-beyond', contentExtendsBeyond);
            modalContentRef.current.style.setProperty('--content-extends-above', contentExtendsAbove);
            modalContentRef.current.style.setProperty('--scroll-track-height', `${trackHeight}%`);
          }
        });
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    // Store current scroll position
    const scrollY = window.scrollY;
    
    // Mark that modal is open for navigation detection
    window.modalIsOpen = true;
    
    document.addEventListener("keydown", handleKeyDown);
    
    const modalContent = modalContentRef.current;
    if (modalContent) {
      modalContent.addEventListener('scroll', updateScrollbar, { passive: true });
      updateScrollbar(); // Initial calculation
      
      // Handle dynamic content changes
      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateScrollbar);
      });
      resizeObserver.observe(modalContent);
      
      // Cleanup ResizeObserver
      return () => {
        resizeObserver.disconnect();
      };
    }
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (modalContent) {
        modalContent.removeEventListener('scroll', updateScrollbar);
      }
      
      // Restore scroll position
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollY,
          left: 0,
          behavior: 'instant'
        });
      });
      
      // Clear the flag
      delete window.modalIsOpen;
    };
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="modal" 
      role="dialog" 
      aria-modal="true" 
      tabIndex="-1"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalContentRef}
        className="modal-content"
        style={{
          '--scroll-thumb-height': `${scrollData.thumbHeight}%`,
          '--scroll-thumb-top': `${scrollData.thumbTop}%`
        }}
      >
          <button 
            className="modal__close" 
            onClick={onClose} 
            aria-label="Close Modal"
          >
            <FaTimes aria-hidden="true" />
          </button>
          
          <div className="modal__header">
            <h2 className="modal__title">{project.title}</h2>
            <div className="modal__category">
              <FaTag className="category__icon" aria-hidden="true" />
              <span>{project.category}</span>
            </div>
          </div>
          
          <div className="modal__image">
            <img 
              src={project.image} 
              alt={project.alt} 
              className="modal__img"
            />
          </div>
          
          <div className="modal__content">
            <p className="modal__description">{project.longDescription}</p>
            
            <div className="modal__actions">
              <a 
                href={project.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="modal__btn modal__btn--primary"
                aria-label={`Visit ${project.title} project`}
              >
                <FaExternalLinkAlt aria-hidden="true" />
                <span>Visit Project</span>
              </a>
              
              <button 
                className="modal__btn modal__btn--secondary" 
                onClick={onClose}
                aria-label="Close modal"
              >
                <FaTimes aria-hidden="true" />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      </div>
  );
});

export default ProjectModal;
